'use client';

/**
 * GoogleReviewsHowItWorks — 4-step customer journey using shared
 * StepCards. The LP-card screenshot + badge zoom sit underneath as a
 * "context strip" so visitors see the Google review badge in context
 * (not floating in isolation).
 */

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import GoogleReviewBadgePreview from './GoogleReviewBadgePreview';
import SectionLabel from '../home/SectionLabel';
import StepCards, { StepCardItem } from '../_shared/sections/StepCards';
import { useTranslation } from '@/i18n/client';
import { HiArrowRight, HiCursorArrowRays, HiHandRaised, HiQrCode } from 'react-icons/hi2';

type Props = { lang: Locale };

type StepKey = 'scan' | 'tap' | 'route';
type StepCopy = { num: string; title: string; description: string; details?: string[] };

const STEP_NUMS: Record<StepKey, string> = {
  scan: '01',
  tap: '02',
  route: '03',
};

const STEP_ICONS: Record<StepKey, StepCardItem['Icon']> = {
  scan: HiQrCode,
  tap: HiCursorArrowRays,
  route: HiHandRaised,
};

const KEYS: StepKey[] = ['scan', 'tap', 'route'];

const GoogleReviewsHowItWorks = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const copyFor = (k: StepKey): StepCopy => {
    const details =
      k === 'route' ? (t('GoogleReviews/HowItWorks/route/details', { returnObjects: true }) as string[]) : undefined;
    return {
      num: STEP_NUMS[k],
      title: t(`GoogleReviews/HowItWorks/${k}/title`),
      description: t(`GoogleReviews/HowItWorks/${k}/body`),
      details: Array.isArray(details) ? details : undefined,
    };
  };

  const steps: StepCardItem[] = KEYS.map((k) => {
    const c = copyFor(k);
    return {
      key: k,
      num: c.num,
      title: c.title,
      description: c.description,
      details: c.details,
      Icon: STEP_ICONS[k],
      footer:
        k === 'scan' ? (
          <Link
            href={`/${lang}/loyalty-programs`}
            className="mono inline-flex items-center gap-1.5"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: '#22D3EE',
              textDecoration: 'none',
              alignSelf: 'flex-start',
            }}
          >
            {t('GoogleReviews/HowItWorks/scan_link_label')}
            <HiArrowRight className="w-3 h-3" />
          </Link>
        ) : undefined,
    };
  });

  return (
    <section
      id="how-it-works"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="02" label={t('GoogleReviews/HowItWorks/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('GoogleReviews/HowItWorks/title1')}{' '}
          <span style={{ color: '#B4C1D8' }}>{t('GoogleReviews/HowItWorks/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('GoogleReviews/HowItWorks/subtitle')}
        </p>

        <StepCards steps={steps} accent="#22D3EE" minCardWidth={240} showArrow />

        <div
          className="mt-12 grid gap-6 items-center"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
        >
          <div
            className="rounded-2xl overflow-hidden relative w-full justify-self-center"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              aspectRatio: '4 / 5',
              maxWidth: 360,
            }}
          >
            <Image
              src="https://cdn.1cdn.app/application/UMD/26050207423880_sum_lp_card.png"
              alt={t('GoogleReviews/HowItWorks/card_alt')}
              fill
              sizes="(max-width: 1024px) 100vw, 360px"
              className="object-contain p-4"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="flex-shrink-0">
              <GoogleReviewBadgePreview ariaLabel={t('GoogleReviews/HowItWorks/badge_aria')} />
            </div>
            <div className="flex-1">
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  letterSpacing: 1.5,
                  color: '#22D3EE',
                  textTransform: 'uppercase',
                  marginBottom: 4,
                }}
              >
                {t('GoogleReviews/HowItWorks/badge_label')}
              </div>
              <p
                style={{
                  color: '#B4C1D8',
                  fontSize: 14,
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {t('GoogleReviews/HowItWorks/badge_body')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsHowItWorks;
